import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import {
  deleteReading,
  resetReading,
  updateReading,
} from "../../../services/readingOrdersApi";
import { trackEvent } from "../../../shared/analytics";
import { setOrderPosition } from "../actions";

const ReadingOrderActions = ({
  readingId,
  openModal,
  readingOrder,
  orderPosition,
  isReadOnly,
  resetOnly = false,
  onReset,
  onSetGoal,
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isPremium } = usePremium();
  const deleteMutation = useMutation((readingOrderId) => {
    return deleteReading(readingOrderId);
  });
  const resetMutation = useMutation(
    (readingId) => {
      return resetReading(readingId);
    },
    {
      onSuccess: (response, readingId) => {
        dispatch(setOrderPosition(readingId, null));
        queryClient.invalidateQueries(["readingOrder", readingId]);
        onReset?.(response);
      },
    }
  );

  const updateMutation = useMutation(
    ({ reading }) => {
      return updateReading(reading.id, reading);
    },
    {
      onSuccess: (response, { reading }) => {
        dispatch(setOrderPosition(readingId, null));
        queryClient.invalidateQueries(["readingOrder", readingId]);
        window.location.reload();
      },
    }
  );

  return (
    <Menu>
      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
        <HamburgerIcon />
      </MenuButton>
      <MenuList>
        {!!onSetGoal && !resetOnly && (
          <MenuItem
            onClick={() => {
              if (isPremium) {
                trackEvent("list_nav_set_goal_click", {
                  reading_id: readingId,
                });
                onSetGoal();
                return;
              }
              trackEvent("upgrade_clicked", { source: "set_goal" });
              navigate("/go-premium");
            }}
          >
            {isPremium ? "Set a goal" : "Set a goal · Premium"}
          </MenuItem>
        )}
        {!resetOnly &&
          (!!orderPosition ? (
            <MenuItem
              onClick={() => {
                trackEvent("jump_back", { reading_id: readingId });
                updateMutation.mutate({
                  reading: {
                    id: readingId,
                    orderPosition: null,
                  },
                });
              }}
              isDisabled={!isPremium || isReadOnly}
            >
              Jump back
            </MenuItem>
          ) : (
            <MenuItem onClick={openModal} isDisabled={!isPremium || isReadOnly}>
              Jump to...
            </MenuItem>
          ))}
        <MenuItem
          isDisabled={resetOnly ? isReadOnly : !isPremium || isReadOnly}
          onClick={() => {
            if (
              window.confirm(
                "All comics in this list will be marked as unread, it may decrease your progress of other reading orders crossing over. This cannot be undone."
              )
            ) {
              resetMutation.mutateAsync(readingId).then(() => {
                trackEvent("reading_order_reset", {
                  reading_id: readingId,
                  reading_order_id: readingOrder?.id,
                });
                toast({
                  title: "Reading order reset.",
                  description: "Your list was reset successfully.",
                  status: "success",
                  duration: 5000,
                  isClosable: true,
                });
              });
            }
          }}
        >
          Reset reading order
        </MenuItem>
        {!resetOnly && (
          <MenuItem
            onClick={() => {
              if (
                window.confirm(
                  "This reading order will be removed from your reading list. Are you sure?"
                )
              ) {
                deleteMutation.mutateAsync(readingId).then(() => {
                  trackEvent("reading_order_removed", {
                    reading_id: readingId,
                    reading_order_id: readingOrder?.id,
                  });
                  navigate("/reading", {
                    state: {
                      franchiseTab: readingOrder.franchise,
                    },
                  });
                  toast({
                    title: "Reading order removed.",
                    description:
                      "The reading order was removed from your reading.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });
                });
              }
            }}
          >
            Remove from Reading
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

export default ReadingOrderActions;
